import {
  Button,
  styled,
  Tooltip,
  Typography,
  CircularProgress,
} from '@mui/material';
import { Row } from 'further-ui/components/Layout';
import useReviewActions from './useReviewActions';
import {
  OnboardingStep,
  OnboardingStepStatus,
} from 'further-types/lp/onboarding';
import { CheckCircle } from '@mui/icons-material';
import RequestMoreInformationForm from './RequestMoreInformationForm';
import { Disclosure, useDisclosure } from 'further-ui/hooks';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';

const ApproveButton = styled(Button)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(theme.spacingSizes.xs),
  '&:disabled': {
    backgroundColor: theme.palette.grey[900],
    color: 'white',
  },
}));

type Props = {
  vehicleId: string;
  vehicleApplicationId: string;
  step: {
    key: OnboardingStep;
    canUnapprove: boolean;
    status?: OnboardingStepStatus | null;
  };
  manualKycDisclosure: Disclosure;
};

const ReviewActions: React.FC<Props> = ({
  vehicleId,
  vehicleApplicationId,
  step,
  manualKycDisclosure,
}) => {
  const {
    showResetStepButton,
    showManualKycButton,
    showRequestMoreInformationButton,
    manualKycButtonLabel,
    approveButtonLabel,
    handleApproveUnapprove,
    isRequestMoreInformationFormOpen,
    openRequestMoreInformationForm,
    closeRequestMoreInformationForm,
    isApproved,
    isPending,
    showApproveButton,
    handleResetStep,
    handleRequestMoreInformation,
    isApproveOrRequestMoreInformationPending,
  } = useReviewActions(vehicleApplicationId, vehicleId, step);

  const resetStepDisclosure = useDisclosure();

  if (
    !showResetStepButton &&
    !showApproveButton &&
    !showRequestMoreInformationButton &&
    !showManualKycButton
  ) {
    return null;
  }

  if (isRequestMoreInformationFormOpen) {
    return (
      <RequestMoreInformationForm
        onSubmitForm={handleRequestMoreInformation}
        onClose={closeRequestMoreInformationForm}
        isSubmitting={isApproveOrRequestMoreInformationPending}
      />
    );
  }

  const unapproveTooltipText =
    isApproved && step.canUnapprove ? 'Click to unapprove this step.' : null;

  return (
    <>
      <Row centered fullWidth spacing="sm" justify="flex-end">
        {showRequestMoreInformationButton ? (
          <Button
            variant="outlined"
            color="primary"
            size="large"
            onClick={openRequestMoreInformationForm}
          >
            Request additional information
          </Button>
        ) : null}
        {showResetStepButton ? (
          <Button
            variant="outlined"
            color="primary"
            size="large"
            onClick={resetStepDisclosure.onOpen}
          >
            {step.key === OnboardingStep.KYCOrKYB ? 'Reset KYC' : 'Reset step'}
          </Button>
        ) : null}
        {showManualKycButton ? (
          <Button
            variant="outlined"
            color="primary"
            size="large"
            onClick={() => {
              manualKycDisclosure.onOpen();
            }}
          >
            {manualKycButtonLabel}
          </Button>
        ) : null}

        {showApproveButton ? (
          <Tooltip title={unapproveTooltipText} placement="top">
            <ApproveButton
              variant="contained"
              color="primary"
              size="large"
              onClick={handleApproveUnapprove}
              disabled={isPending || (isApproved && !step.canUnapprove)}
            >
              {approveButtonLabel}
              {isApproved ? (
                <CheckCircle color="primary" fontSize="small" />
              ) : null}
              {isPending ? <CircularProgress size={14} /> : null}
            </ApproveButton>
          </Tooltip>
        ) : null}
      </Row>
      <ConfirmDialog
        open={resetStepDisclosure.isOpen}
        onClose={resetStepDisclosure.onClose}
        title="Resetting step"
        content={
          <Typography variant="body2">
            Resetting this step means the investor must recomplete it. This
            cannot be undone.
          </Typography>
        }
        actions={{
          confirm: {
            label: 'Reset step',
            color: 'error',
            onClick: () => {
              resetStepDisclosure.onClose();
              handleResetStep();
            },
          },
        }}
      />
    </>
  );
};

export default ReviewActions;
